/**
 * Theorem metadata — TypeScript SSOT for all ten GuaranteeMeta objects.
 *
 * Data derives from: registries/theorems/shared-theorem-meta.json (CRS canonical source,
 * key "shared-theorem-meta"). Field names follow TypeScript GuaranteeMeta conventions
 * (camelCase) rather than the canonical JSON snake_case.
 *
 * See: governance/Data-Code-Separation-Policy.md
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

// inline: typed — TypeScript SSOT projection of registries/theorems/shared-theorem-meta.json.
// JSON import impractical: rootDir constraint prevents importing from outside src/;
// TypeScript camelCase field names differ from canonical snake_case JSON keys.
// Validated against the canonical registry at test time (registry-fidelity.test.ts).

import type { GuaranteeMeta } from "../foundation/types.js";

// ── Compositional guarantees (IMMUNE mode) ─────────────────────

/** PERMIT (P) — Slice category. Neutralizes ambient authority (G1). */
export const PERMIT_META = {
  code: "P",
  name: "PERMIT",
  legacy: "AUTH",
  structure: "Slice category",
  safetyClass: "compositional",
  mode: "immune",
  tier: 1,
  seamType: "mixed",
  generator: "G1",
  agdaSource: "Kernel/PERMIT/Neutralization.agda",
} as const satisfies GuaranteeMeta;

/** SEPARATE (S) — Double category. Neutralizes data-instruction confusion (G2). */
export const SEPARATE_META = {
  code: "S",
  name: "SEPARATE",
  legacy: "BOUND",
  structure: "Double category",
  safetyClass: "compositional",
  mode: "immune",
  tier: 2,
  seamType: "diagonal",
  generator: "G2",
  agdaSource: "Kernel/SEPARATE/Neutralization.agda",
} as const satisfies GuaranteeMeta;

/** DATA.ORIGIN (D1) — Fibration. Neutralizes provenance forgery (G2). */
export const DATA_ORIGIN_META = {
  code: "D1",
  name: "DATA.ORIGIN",
  legacy: "INFO",
  structure: "Fibration",
  safetyClass: "compositional",
  mode: "immune",
  tier: 2,
  seamType: "vertical",
  generator: "G2",
  agdaSource: "Kernel/DATA/ORIGIN/Neutralization.agda",
} as const satisfies GuaranteeMeta;

/** DATA.REACH (D2) — Opfibration. Neutralizes data reachability violations (G2). */
export const DATA_REACH_META = {
  code: "D2",
  name: "DATA.REACH",
  legacy: "",
  structure: "Opfibration",
  safetyClass: "compositional",
  mode: "immune",
  tier: 2,
  seamType: "horizontal",
  generator: "G2",
  agdaSource: "Kernel/DATA/REACH/Neutralization.agda",
} as const satisfies GuaranteeMeta;

/** PRECOMMIT (W) — Graded monad. Neutralizes time reversal via effect grades (G3). */
export const PRECOMMIT_META = {
  code: "W",
  name: "PRECOMMIT",
  legacy: "IRREV",
  structure: "Graded monad",
  safetyClass: "compositional",
  mode: "immune",
  tier: 1,
  seamType: "vertical",
  generator: "G3",
  agdaSource: "Kernel/PRECOMMIT/Neutralization.agda",
} as const satisfies GuaranteeMeta;

// ── Parametric guarantees (CONTAINED / INERT mode) ─────────────

/** RES.CONSUME (R1) — Linear category. Neutralizes resource exhaustion (G4). */
export const RES_CONSUME_META = {
  code: "R1",
  name: "RES.CONSUME",
  legacy: "RES",
  structure: "Linear category",
  safetyClass: "parametric",
  mode: "contained",
  tier: 3,
  seamType: "horizontal",
  generator: "G4",
  agdaSource: "Kernel/RES/CONSUME/Neutralization.agda",
} as const satisfies GuaranteeMeta;

/** RES.RELEASE (R2) — Compact closed. Neutralizes resource leaks (G4). */
export const RES_RELEASE_META = {
  code: "R2",
  name: "RES.RELEASE",
  legacy: "",
  structure: "Compact closed",
  safetyClass: "parametric",
  mode: "contained",
  tier: 3,
  seamType: "horizontal",
  generator: "G4",
  agdaSource: "Kernel/RES/RELEASE/Neutralization.agda",
} as const satisfies GuaranteeMeta;

/** COMPOSE (C) — Operad. Neutralizes unsafe composition of capabilities (G1). */
export const COMPOSE_META = {
  code: "C",
  name: "COMPOSE",
  legacy: "COMP",
  structure: "Operad",
  safetyClass: "parametric",
  mode: "inert",
  tier: 1,
  seamType: "horizontal",
  generator: "G1",
  agdaSource: "Kernel/COMPOSE/Neutralization.agda",
} as const satisfies GuaranteeMeta;

/** INV.ESTABLISH (I1) — F-algebra. Neutralizes unestablished invariants (G3). */
export const INV_ESTABLISH_META = {
  code: "I1",
  name: "INV.ESTABLISH",
  legacy: "",
  structure: "F-algebra",
  safetyClass: "parametric",
  mode: "inert",
  tier: 2,
  seamType: "vertical",
  generator: "G3",
  agdaSource: "Kernel/INV/ESTABLISH/Neutralization.agda",
} as const satisfies GuaranteeMeta;

/** INV.PRESERVE (I2) — F-coalgebra. Neutralizes invariant-breaking transitions (G3). */
export const INV_PRESERVE_META = {
  code: "I2",
  name: "INV.PRESERVE",
  legacy: "TEMP",
  structure: "F-coalgebra",
  safetyClass: "parametric",
  mode: "inert",
  tier: 2,
  seamType: "horizontal",
  generator: "G3",
  agdaSource: "Kernel/INV/PRESERVE/Neutralization.agda",
} as const satisfies GuaranteeMeta;
